import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getVenueVisits } from '../../services/visitService';
import { getLocations } from '../../services/venueService';
import { usePortal } from '../../context/PortalContext';
import VenueSelector from '../../components/VenueSelector/VenueSelector';
import VenueEmptyState from '../../components/EmptyState/VenueEmptyState';
import VisitEmptyState from '../../components/EmptyState/VisitEmptyState';
import './DeveloperPortalLayout.css';

interface VisitData {
  id: string;
  user_id: string;
  venue_id: string;
  location_id?: string;
  check_in_time: string;
  check_out_time?: string;
  status: 'active' | 'expired';
  visit_type: 'GENERAL' | 'LOCATION_SPECIFIC' | 'ONLINE';
  expires_at: string;
  user?: {
    display_name: string;
    first_name: string;
    last_name: string;
    dietary_preferences?: string[];
    health_concerns?: string[];
    ingredient_preferences?: {
      ingredient_name: string;
      preference_type: 'like' | 'dislike';
    }[];
    favorites?: {
      id: string;
      item_id: string;
      item: {
        id: string;
        name: string;
        type: 'food' | 'drink';
      };
    }[];
  };
}

// Format snake_case strings to user-friendly Title Case
const formatPreferenceLabel = (label: string): string => {
  return label
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .replace(/Fodmap/g, 'FODMAP'); // Handle special acronyms
};

const DataExplorer: React.FC = () => {
  const { user } = useAuth();
  const { venues, loadVenues } = usePortal();
  const [visits, setVisits] = useState<VisitData[]>([]);
  const [selectedVenueId, setSelectedVenueId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVisit, setSelectedVisit] = useState<VisitData | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [locations, setLocations] = useState<{[key: string]: string}>({});

  useEffect(() => {
    loadVenues();
  }, []);

  useEffect(() => {
    if (selectedVenueId) {
      loadVisitsForVenue();
    }
  }, [selectedVenueId]);

  useEffect(() => {
    if (venues.length > 0 && !selectedVenueId) {
      setSelectedVenueId(venues[0].id);
    }
  }, [venues, selectedVenueId]);

  const loadVisitsForVenue = async () => {
    try {
      setLoading(true);
      const [visitsData, locationsData] = await Promise.all([
        getVenueVisits(selectedVenueId, 1, 100, true), // unique_visits_only=true
        getLocations(selectedVenueId)
      ]);
      
      setVisits(visitsData.items || []);
      
      // Create a map of location_id to location_name
      const locationMap: {[key: string]: string} = {};
      locationsData.forEach(location => {
        locationMap[location.id] = location.label;
      });
      setLocations(locationMap);
    } catch (err) {
      setError('Failed to load visits');
      console.error('Visits error:', err);
    } finally {
      setLoading(false);
    }
  };


  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString();
  };

  const formatPreferences = (items: string[], maxItems: number = 1) => {
    if (!items || items.length === 0) {
      return <span className="no-preferences">---</span>;
    }
    
    if (items.length <= maxItems) {
      return (
        <div className="preferences-list">
          {items.map((item, index) => (
            <span key={index} className="preference-tag dietary">
              {item}
            </span>
          ))}
        </div>
      );
    }
    
    return (
      <span className="preference-count">
        {items[0]} +{items.length - 1} more
      </span>
    );
  };

  const formatHealthConcerns = (concerns: string[], maxItems: number = 1) => {
    if (!concerns || concerns.length === 0) {
      return <span className="no-preferences">---</span>;
    }
    
    if (concerns.length <= maxItems) {
      return (
        <div className="preferences-list">
          {concerns.map((concern, index) => (
            <span key={index} className="preference-tag health">
              {concern}
            </span>
          ))}
        </div>
      );
    }
    
    return (
      <span className="preference-count">
        {concerns[0]} +{concerns.length - 1} more
      </span>
    );
  };

  const formatIngredientPreferences = (preferences: {ingredient_name: string, preference_type: string}[], maxItems: number = 1) => {
    if (!preferences || preferences.length === 0) {
      return <span className="no-preferences">---</span>;
    }
    
    if (preferences.length <= maxItems) {
      return (
        <div className="preferences-list">
          {preferences.map((pref, index) => (
            <span key={index} className={`preference-tag ${pref.preference_type}`}>
              {pref.ingredient_name}
            </span>
          ))}
        </div>
      );
    }
    
    return (
      <span className="preference-count">
        {preferences[0].ingredient_name} +{preferences.length - 1} more
      </span>
    );
  };

  const handleRowClick = (visit: VisitData) => {
    setSelectedVisit(visit);
    setShowUserModal(true);
  };

  const closeUserModal = () => {
    setShowUserModal(false);
    setSelectedVisit(null);
  };

  const selectedVenue = venues.find(v => v.id === selectedVenueId);

  if (loading) {
    return (
      <div className="container">
        {/* Header with inline venue selector */}
        <div className="charts-header">
          <div className="charts-header-content">
            <div className="charts-title-section">
              <h3>Data Explorer</h3>
              <p>Explore detailed visit data and user preferences for your venues.</p>
            </div>
            
            <div className="charts-selectors">
              <VenueSelector
                venues={venues}
                selectedVenueId={selectedVenueId}
                onVenueChange={setSelectedVenueId}
                label="Select Venue"
                placeholder="Choose a venue to explore data..."
                className="compact inline"
              />
            </div>
          </div>
        </div>
        <div className="loading">Loading data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        {/* Header with inline venue selector */}
        <div className="charts-header">
          <div className="charts-header-content">
            <div className="charts-title-section">
              <h3>Data Explorer</h3>
              <p>Explore detailed visit data and user preferences for your venues.</p>
            </div>
            
            <div className="charts-selectors">
              <VenueSelector
                venues={venues}
                selectedVenueId={selectedVenueId}
                onVenueChange={setSelectedVenueId}
                label="Select Venue"
                placeholder="Choose a venue to explore data..."
                className="compact inline"
              />
            </div>
          </div>
        </div>
        <div className="error">{error}</div>
      </div>
    );
  }

  // Show venue empty state if no venues
  if (venues.length === 0) {
    return (
      <div className="container">
        <div className="charts-header">
          <div className="charts-header-content">
            <div className="charts-title-section">
              <h3>Data Explorer</h3>
              <p>Explore detailed visit data and user preferences for your venues.</p>
            </div>
          </div>
        </div>
        <VenueEmptyState onAddVenue={() => window.location.href = '/portal'} />
      </div>
    );
  }

  // Show visit empty state if no visits for selected venue
  if (selectedVenueId && visits.length === 0 && !loading) {
    return (
      <div className="container">
        <div className="charts-header">
          <div className="charts-header-content">
            <div className="charts-title-section">
              <h3>Data Explorer</h3>
              <p>Explore detailed visit data and user preferences for your venues.</p>
            </div>
            
            <div className="charts-selectors">
              <VenueSelector
                venues={venues}
                selectedVenueId={selectedVenueId}
                onVenueChange={setSelectedVenueId}
                label="Select Venue"
                placeholder="Choose a venue to explore data..."
                className="compact inline"
              />
            </div>
          </div>
        </div>
        <VisitEmptyState onViewVenues={() => window.location.href = '/portal'} />
      </div>
    );
  }

  return (
    <div className="container">
      {/* Header with inline venue selector */}
      <div className="charts-header">
        <div className="charts-header-content">
          <div className="charts-title-section">
            <h3>Data Explorer</h3>
            <p>Explore detailed visit data and user preferences for your venues.</p>
          </div>
          
          <div className="charts-selectors">
            <VenueSelector
              venues={venues}
              selectedVenueId={selectedVenueId}
              onVenueChange={setSelectedVenueId}
              label="Select Venue"
              placeholder="Choose a venue to explore data..."
              className="compact inline"
            />
          </div>
        </div>
      </div>


      {/* Visits Table */}
      <div className="section">
        <h2>Visitors</h2>
        {visits.length > 0 ? (
          <div className="visitsTable">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Check-in Time</th>
                  <th>Dietary Preferences</th>
                  <th>Health Concerns</th>
                  <th>Ingredient Preferences</th>
                </tr>
              </thead>
              <tbody>
                {visits.map((visit) => (
                  <tr key={visit.id} className="clickable-row" onClick={() => handleRowClick(visit)}>
                    <td>
                      {visit.user?.display_name || 'Unknown User'}
                    </td>
                    <td>
                      <span className={`badge ${visit.visit_type}`}>
                        {visit.visit_type === 'ONLINE' ? 'Online' : 
                         visit.location_id ? locations[visit.location_id] || 'Unknown Location' : 'General'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${visit.status}`}>
                        {visit.status}
                      </span>
                    </td>
                    <td>{formatTime(visit.check_in_time)}</td>
                    <td>
                      {formatPreferences(visit.user?.dietary_preferences || [])}
                    </td>
                    <td>
                      {formatHealthConcerns(visit.user?.health_concerns || [])}
                    </td>
                    <td>
                      {formatIngredientPreferences(visit.user?.ingredient_preferences || [])}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No visits found for this venue.</p>
        )}
      </div>

      {/* User Detail Modal */}
      {showUserModal && selectedVisit && (
        <div className="user-modal-overlay" onClick={closeUserModal}>
          <div className="user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="user-modal-header">
              <h2>User Details</h2>
              <button className="modal-close" onClick={closeUserModal}>
                ×
              </button>
            </div>
            
            <div className="user-modal-content">
              <div className="user-info-section">
                <h3>User Information</h3>
                <div className="user-details">
                  <div className="detail-item">
                    <label>Name:</label>
                    <span>{selectedVisit.user?.display_name || 'Unknown User'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Location:</label>
                    <span className={`badge ${selectedVisit.visit_type}`}>
                      {selectedVisit.visit_type === 'ONLINE' ? 'Online' : 
                       selectedVisit.location_id ? locations[selectedVisit.location_id] || 'Unknown Location' : 'General'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span className={`badge ${selectedVisit.status}`}>
                      {selectedVisit.status}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Check-in Time:</label>
                    <span>{formatTime(selectedVisit.check_in_time)}</span>
                  </div>
                </div>
              </div>

              <div className="preferences-section">
                <h3>Dietary Preferences</h3>
                <div className="preferences-grid">
                  {selectedVisit.user?.dietary_preferences && selectedVisit.user.dietary_preferences.length > 0 ? (
                    selectedVisit.user.dietary_preferences.map((pref, index) => (
                      <span key={index} className="preference-tag dietary">
                        {formatPreferenceLabel(pref)}
                      </span>
                    ))
                  ) : (
                    <span className="no-preferences">---</span>
                  )}
                </div>
              </div>

              <div className="preferences-section">
                <h3>Health Concerns</h3>
                <div className="preferences-grid">
                  {selectedVisit.user?.health_concerns && selectedVisit.user.health_concerns.length > 0 ? (
                    selectedVisit.user.health_concerns.map((concern, index) => (
                      <span key={index} className="preference-tag health">
                        {formatPreferenceLabel(concern)}
                      </span>
                    ))
                  ) : (
                    <span className="no-preferences">---</span>
                  )}
                </div>
              </div>

              <div className="preferences-section">
                <h3>Ingredient Preferences</h3>
                {selectedVisit.user?.ingredient_preferences && selectedVisit.user.ingredient_preferences.length > 0 ? (
                  (() => {
                    // Group preferences by type
                    const groupedPreferences = selectedVisit.user.ingredient_preferences.reduce((acc, pref) => {
                      if (!acc[pref.preference_type]) {
                        acc[pref.preference_type] = [];
                      }
                      acc[pref.preference_type].push(pref.ingredient_name);
                      return acc;
                    }, {} as Record<string, string[]>);

                    return (
                      <div className="ingredient-preferences-grouped">
                        {Object.entries(groupedPreferences).map(([type, ingredients]) => (
                          <div key={type} className="preference-group">
                            <h4 className="preference-group-title">
                              {type.charAt(0).toUpperCase() + type.slice(1)}:
                            </h4>
                            <div className="preferences-grid">
                              {ingredients.map((ingredient, index) => (
                                <span key={index} className={`preference-tag ${type}`}>
                                  {ingredient}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()
                ) : (
                  <span className="no-preferences">---</span>
                )}
              </div>

              <div className="preferences-section">
                <h3>Favorite Foods & Drinks</h3>
                <div className="favorites-grouped">
                  {/* Favorite Foods Section */}
                  <div className="preference-group">
                    <h4 className="preference-group-title">Favorite Foods:</h4>
                    <div className="preferences-grid">
                      {selectedVisit.user?.favorites && selectedVisit.user.favorites.length > 0 ? (
                        selectedVisit.user.favorites
                          .filter(fav => fav.item.type === 'food')
                          .map((fav, index) => (
                            <span key={index} className="preference-tag food">
                              {fav.item.name}
                            </span>
                          ))
                      ) : null}
                      {(!selectedVisit.user?.favorites || 
                        !selectedVisit.user.favorites.some(fav => fav.item.type === 'food')) && (
                        <span className="no-preferences">No favorite foods</span>
                      )}
                    </div>
                  </div>

                  {/* Favorite Drinks Section */}
                  <div className="preference-group">
                    <h4 className="preference-group-title">Favorite Drinks:</h4>
                    <div className="preferences-grid">
                      {selectedVisit.user?.favorites && selectedVisit.user.favorites.length > 0 ? (
                        selectedVisit.user.favorites
                          .filter(fav => fav.item.type === 'drink')
                          .map((fav, index) => (
                            <span key={index} className="preference-tag drink">
                              {fav.item.name}
                            </span>
                          ))
                      ) : null}
                      {(!selectedVisit.user?.favorites || 
                        !selectedVisit.user.favorites.some(fav => fav.item.type === 'drink')) && (
                        <span className="no-preferences">No favorite drinks</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataExplorer;
